import { useMemo } from 'react';
import { AxiosError } from 'axios';
import { useToast } from '@heidi-pay/heidi-common-fe/hooks';
import { IError } from '@heidi-pay/heidi-common-fe/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useHasPermission } from '@hooks/useHasPermission';
import { useI18n } from '@hooks/useI18n/useI18n';
import { ICustomModalButton } from '@hooks/useModal/components/types';
import { usePortalError } from '@hooks/usePortalError';
import { IOpsPortalEarlySettlement } from '@schemas/opsPortal/types/earlySettlement';
import { QueryKeys } from '@utils/constants';
import { ButtonVariant, Endpoints } from '@utils/enums';
import { postDataToEndpoint } from '@utils/postDataToEndpoint';
import { EarlySettlementActionType, EarlySettlementStatus } from './enums';
import { IEarlySettlementDisplayModalRequestData } from './types';

interface IUseGetEarlySettlementButtons {
  closeModal: () => void;
  earlySettlement: IOpsPortalEarlySettlement | undefined;
  earlySettlementQuoteUuid: string | undefined;
  externalContractUUID: string | undefined;
}

interface IUseGetEarlySettlementButtonsReturn {
  isSubmitting: boolean;
  modalButtons: ICustomModalButton[];
}

export const useGetEarlySettlementButtons = ({
  closeModal,
  earlySettlement,
  earlySettlementQuoteUuid,
  externalContractUUID,
}: IUseGetEarlySettlementButtons): IUseGetEarlySettlementButtonsReturn => {
  const hasAcceptPermission = useHasPermission([
    'ops_portal.accept_early_settlement',
  ]);
  const hasCancelPermission = useHasPermission([
    'ops_portal.cancel_early_settlement',
  ]);

  const { handleAxiosError } = usePortalError();
  const { translate } = useI18n();
  const queryClient = useQueryClient();
  const { message } = useToast(translate);

  const mutationEndpoints = {
    [EarlySettlementActionType.Accept]: Endpoints.AcceptEarlySettlement,
    [EarlySettlementActionType.Cancel]: Endpoints.CancelEarlySettlement,
    [EarlySettlementActionType.Send]: Endpoints.SendEarlySettlementToCustomer,
  };

  const useEarlySettlementMutation = (
    endpoint: EarlySettlementActionType,
    successMessage: string,
  ) =>
    useMutation<
      unknown,
      AxiosError<IError>,
      IEarlySettlementDisplayModalRequestData
    >(
      () =>
        postDataToEndpoint({
          endpoint: `${mutationEndpoints[endpoint]}/${earlySettlementQuoteUuid}`,
        }),
      {
        onSuccess: () => {
          message.success(successMessage);
          queryClient.invalidateQueries([QueryKeys.EARLY_SETTLEMENTS]);
          queryClient.invalidateQueries([
            QueryKeys.EARLY_SETTLEMENT_DISPLAY,
            earlySettlementQuoteUuid,
          ]);
          queryClient.invalidateQueries([
            QueryKeys.MONETA_CONTRACT_DATA,
            externalContractUUID,
          ]);
          closeModal?.();
        },
        onError: error => {
          handleAxiosError(error);
        },
      },
    );

  const { isLoading: isSubmittingAccepted, mutate: handleAcceptClick } =
    useEarlySettlementMutation(
      EarlySettlementActionType.Accept,
      'OPS_PORTAL.STATUS.EARLY_SETTLEMENT_QUOTE_SUCCESSFULLY_ACCEPTED',
    );

  const { isLoading: isSubmittingCancelled, mutate: handleCancelClick } =
    useEarlySettlementMutation(
      EarlySettlementActionType.Cancel,
      'OPS_PORTAL.STATUS.EARLY_SETTLEMENT_QUOTE_SUCCESSFULLY_CANCELLED',
    );

  const {
    isLoading: isSubmittingSendToCustomer,
    mutate: handleSendToCustomerClick,
  } = useEarlySettlementMutation(
    EarlySettlementActionType.Send,
    'OPS_PORTAL.STATUS.EARLY_SETTLEMENT_QUOTE_SUCCESSFULLY_SENT_TO_CUSTOMER',
  );

  const isSubmitting =
    isSubmittingAccepted || isSubmittingCancelled || isSubmittingSendToCustomer;

  const cancelButton: ICustomModalButton[] = useMemo(
    () =>
      earlySettlement?.quote_status === EarlySettlementStatus.Open &&
      hasCancelPermission
        ? [
            {
              text: translate('OPS_PORTAL.BUTTON.CANCEL_QUOTE'),
              clickHandler: handleCancelClick,
              variant: ButtonVariant.Danger,
              disabled: isSubmitting,
            },
          ]
        : [],
    [
      earlySettlement?.quote_status,
      handleCancelClick,
      hasCancelPermission,
      isSubmitting,
      translate,
    ],
  );

  const acceptButton: ICustomModalButton[] = useMemo(
    () =>
      earlySettlement?.quote_status === EarlySettlementStatus.Open &&
      hasAcceptPermission
        ? [
            {
              text: translate('OPS_PORTAL.BUTTON.ACCEPT_QUOTE'),
              clickHandler: handleAcceptClick,
              variant: ButtonVariant.Primary,
              disabled: isSubmitting,
            },
          ]
        : [],
    [
      earlySettlement?.quote_status,
      handleAcceptClick,
      hasAcceptPermission,
      isSubmitting,
      translate,
    ],
  );

  const sendButton: ICustomModalButton[] = useMemo(
    () =>
      earlySettlement?.quote_status === EarlySettlementStatus.Open &&
      hasAcceptPermission
        ? [
            {
              text: translate('OPS_PORTAL.BUTTON.SEND_QUOTE_TO_CUSTOMER'),
              clickHandler: handleSendToCustomerClick,
              variant: ButtonVariant.Secondary,
              disabled: isSubmitting,
            },
          ]
        : [],
    [
      earlySettlement?.quote_status,
      handleSendToCustomerClick,
      hasAcceptPermission,
      isSubmitting,
      translate,
    ],
  );

  const modalButtons: ICustomModalButton[] = useMemo(
    () => [...sendButton, ...cancelButton, ...acceptButton],
    [acceptButton, cancelButton, sendButton],
  );

  return {
    isSubmitting,
    modalButtons,
  };
};
